import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useState } from "react";
import { Pencil, Plus, Trash2, CheckCircle2, XCircle, MinusCircle, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { fetchLiveSignals, fetchPublicSignals } from "@/lib/signals-queries";
import {
  archiveSignal,
  deleteLiveSignal,
  deletePublicSignal,
  type ManualCloseResult,
} from "@/lib/admin-mutations";
import { dispatchPushEvent, sendTestPushToSelf } from "@/lib/push.functions";
import { useAuth } from "@/lib/auth-context";
import { useSupabaseAccessToken } from "@/hooks/useSupabaseSessionReady";
import { authedFetch } from "@/lib/authed-fetch";
import { useEffect } from "react";

export const Route = createFileRoute("/_authenticated/admin/signals/")({
  component: SignalManager,
});

type Source = "live" | "public";

function SignalManager() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Gate queries on the supabase-js client actually having a JWT attached.
  // Without this, fetches can leave with the anon key and RLS returns 0 rows.
  const accessToken = useSupabaseAccessToken();
  const authReady = !!accessToken && !!user;

  const live = useQuery({
    queryKey: ["admin", "live", user?.id],
    queryFn: fetchLiveSignals,
    enabled: authReady,
  });
  const pub = useQuery({
    queryKey: ["admin", "public", user?.id],
    queryFn: fetchPublicSignals,
    enabled: authReady,
  });

  // Surface silent failures so an empty table is never mistaken for "no data".
  useEffect(() => {
    if (live.error) toast.error(`Live signals: ${(live.error as Error).message}`);
  }, [live.error]);
  useEffect(() => {
    if (pub.error) toast.error(`Public signals: ${(pub.error as Error).message}`);
  }, [pub.error]);

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ["admin"] });
    qc.invalidateQueries({ queryKey: ["live-signals"] });
    qc.invalidateQueries({ queryKey: ["public-signals"] });
    qc.invalidateQueries({ queryKey: ["featured-trades"] });
    qc.invalidateQueries({ queryKey: ["active-signal-count"] });
  };

  const remove = useMutation({
    mutationFn: async (args: { id: string; source: Source }) =>
      args.source === "live" ? deleteLiveSignal(args.id) : deletePublicSignal(args.id),
    onSuccess: () => {
      toast.success("Signal deleted");
      refetchAll();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Signals</h1>
          <p className="mt-1 text-sm text-muted-foreground">Publish, edit, and close active trading signals.</p>
        </div>
        <div className="flex gap-2">
          <TestPushButton />
          <Button variant="gold" onClick={() => navigate({ to: "/admin/signals/new" })}>
            <Plus className="h-4 w-4" /> New signal
          </Button>
        </div>
      </div>

      <Section
        title="Live (members)"
        rows={(live.data ?? []).map((s) => ({
          id: s.id,
          source: "live" as const,
          asset: s.asset,
          type: s.type,
          timeframe: s.timeframe,
          entry: s.entry,
          sl: s.sl,
          tp1: s.tp1,
          tp2: s.tp2,
          posted: s.postedAt,
        }))}
        loading={!authReady || live.isLoading}
        onDelete={(id) => remove.mutate({ id, source: "live" })}
        onArchive={refetchAll}
      />

      <Section
        title="Public"
        rows={(pub.data ?? []).map((s) => ({
          id: s.id,
          source: "public" as const,
          asset: s.instrument,
          type: s.type,
          timeframe: s.style,
          entry: s.entry ?? null,
          sl: s.sl ?? null,
          tp1: s.tp1 ?? null,
          tp2: s.tp2 ?? null,
          posted: s.postedAt,
        }))}
        loading={!authReady || pub.isLoading}
        onDelete={(id) => remove.mutate({ id, source: "public" })}
        onArchive={refetchAll}
      />
    </div>
  );
}

function TestPushButton() {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="outline"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const r = await sendTestPushToSelf({ fetch: authedFetch });
          if (r.total === 0) {
            toast.warning("No push subscriptions found for your account. Click Enable on the dashboard banner first.");
          } else {
            toast.success(`Test push sent to ${r.sent} of ${r.total} device(s).`);
          }
        } catch (e) {
          toast.error(`Test push failed: ${(e as Error).message}`);
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? "Sending..." : "Send test push"}
    </Button>
  );
}

type Row = {
  id: string;
  source: Source;
  asset: string;
  type: string;
  timeframe: string;
  entry: number | null;
  sl: number | null;
  tp1: number | null;
  tp2: number | null;
  posted: string;
};

function Section({
  title,
  rows,
  loading,
  onDelete,
  onArchive,
}: {
  title: string;
  rows: Row[];
  loading: boolean;
  onDelete: (id: string) => void;
  onArchive: () => void;
}) {
  return (
    <div className="rounded-xl border border-border/60 bg-card">
      <div className="border-b border-border/60 px-5 py-3">
        <h2 className="font-display text-lg font-semibold">{title} <span className="ml-2 text-xs text-muted-foreground">{rows.length} active</span></h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-5 py-3">ID</th>
              <th className="px-5 py-3">Asset</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">TF</th>
              <th className="px-5 py-3">Entry</th>
              <th className="px-5 py-3">SL</th>
              <th className="px-5 py-3">TP1</th>
              <th className="px-5 py-3">TP2</th>
              <th className="px-5 py-3">Posted</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="px-5 py-6 text-center text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} className="px-5 py-6 text-center text-muted-foreground">No active signals.</td></tr>
            ) : rows.map((r) => (
              <tr key={`${r.source}-${r.id}`} className="border-t border-border/60">
                <td className="px-5 py-3 font-mono text-xs">{r.id}</td>
                <td className="px-5 py-3">{r.asset}</td>
                <td className={`px-5 py-3 font-semibold ${r.type === "Buy" ? "text-emerald-500" : "text-rose-500"}`}>{r.type}</td>
                <td className="px-5 py-3 text-muted-foreground">{r.timeframe}</td>
                <td className="px-5 py-3">{r.entry ?? "—"}</td>
                <td className="px-5 py-3">{r.sl ?? "—"}</td>
                <td className="px-5 py-3">{r.tp1 ?? "—"}</td>
                <td className="px-5 py-3">{r.tp2 ?? "—"}</td>
                <td className="px-5 py-3 text-xs text-muted-foreground">{r.posted}</td>
                <td className="px-5 py-3">
                  <div className="flex justify-end gap-1">
                    {r.source === "live" && (
                      <Button asChild size="sm" variant="ghost">
                        <Link to="/admin/signals/$id/edit" params={{ id: r.id }}><Pencil className="h-3.5 w-3.5" /></Link>
                      </Button>
                    )}
                    <ArchiveDialog row={r} onSuccess={onArchive} />
                    <Button size="sm" variant="ghost" onClick={() => {
                      if (confirm(`Delete signal ${r.id}? This cannot be undone.`)) onDelete(r.id);
                    }}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Close outcomes offered on the manual path. "SL after TP1" is the
// member-facing label for a trade that touched TP1 and returned to the stop:
// under Model A that books full risk, but the row should say what happened.
const CLOSE_RESULTS = ["Hit TP1", "Hit TP2", "Hit SL", "SL after TP1", "BE"] as const;

// Pre-fill only where the number is derivable from the levels. Hit TP1 (a
// discretionary close at an arbitrary point) and BE (whatever the founder
// actually realized) must be typed: a blank field asks a question, a wrong
// default answers it.
const DERIVABLE: Record<ManualCloseResult, boolean> = {
  "Hit TP2": true,
  "Hit SL": true,
  "SL after TP1": true,
  "Hit TP1": false,
  BE: false,
};

function ArchiveDialog({ row, onSuccess }: { row: Row; onSuccess: () => void }) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<ManualCloseResult>("Hit TP1");
  const [pips, setPips] = useState<string>("");

  // Auto-suggest pips for derivable outcomes only.
  const suggest = (r: ManualCloseResult) => {
    if (!DERIVABLE[r]) return "";
    if (row.entry == null) return "";
    const dir = row.type === "Buy" ? 1 : -1;
    // Hit SL and SL after TP1 book identical P/L under Model A: full risk.
    const target = r === "Hit TP2" ? row.tp2 : row.sl;
    if (target == null) return "";
    // 1 pip on XAUUSD = 0.10
    const pipFactor = (row.asset || "").toUpperCase().includes("XAU") ? 10 : 10000;
    return Math.round((target - row.entry) * dir * pipFactor).toString();
  };

  const m = useMutation({
    mutationFn: async () => {
      // Dispatch push BEFORE archive removes the live row.
      try {
        await dispatchPushEvent({
          data: {
            signalId: row.id,
            event: "closed",
            result,
            pips: Number(pips),
          },
          fetch: authedFetch,
        });
      } catch (e) {
        console.warn("push dispatch failed", e);
      }
      return archiveSignal({
        signal_id: row.id,
        source: row.source,
        result,
        pips: Number(pips),
      });
    },
    onSuccess: () => {
      toast.success(`Archived as ${result}`);
      setOpen(false);
      setPips("");
      onSuccess();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => {
      setOpen(v);
      if (v) { setResult("Hit TP1"); setPips(suggest("Hit TP1")); }
    }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" title="Close & archive">
          <Target className="h-3.5 w-3.5 text-gold" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Close signal {row.id}</DialogTitle>
          <DialogDescription>Move this signal to trade history with the realized outcome.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Result</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {CLOSE_RESULTS.map((r) => (
                <Button
                  key={r}
                  type="button"
                  variant={result === r ? "gold" : "outline"}
                  size="sm"
                  className="justify-center text-xs"
                  onClick={() => { setResult(r); setPips(suggest(r)); }}
                >
                  {r === "Hit SL" || r === "SL after TP1" ? (
                    <XCircle className="h-3.5 w-3.5 shrink-0" />
                  ) : r === "BE" ? (
                    <MinusCircle className="h-3.5 w-3.5 shrink-0" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                  )}
                  {r}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="pips">Pips (signed: positive for win, negative for loss)</Label>
            <Input id="pips" type="number" value={pips} onChange={(e) => setPips(e.target.value)} placeholder="e.g. 250 or -120" />
            <p className="mt-1.5 text-xs text-muted-foreground">
              {DERIVABLE[result]
                ? "Pre-filled from the signal levels. Overtype if the realized exit differed."
                : result === "BE"
                  ? "Not derivable: enter the realized result of the breakeven exit (usually 0)."
                  : "Not derivable: a discretionary TP1 close has no fixed distance. Enter the realized pips."}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="gold" onClick={() => m.mutate()} disabled={m.isPending || pips === ""}>
            {m.isPending ? "Archiving…" : "Confirm & archive"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
